// src/system/logger.ts

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  id: string;
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: number;
}

let logs: LogEntry[] = [];
const MAX_LOGS = 500;
const listeners: Set<() => void> = new Set();

/* =========================================================
   INTERNAL HELPERS
   ========================================================= */

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error("Logger listener error:", err);
    }
  });
}

function safeData(data: unknown): unknown {
  try {
    JSON.stringify(data);
    return data;
  } catch {
    return "[unserializable]";
  }
}

function addLog(level: LogLevel, message: string, data?: unknown) {
  const entry: LogEntry = {
    id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    level,
    message,
    data: safeData(data),
    timestamp: Date.now(),
  };

  // keep max logs
  logs = [...logs.slice(-(MAX_LOGS - 1)), entry];

  notify();

  /* =========================================================
     CONSOLE OUTPUT (SAFE)
     ========================================================= */

  const prefix = `[Nuvra ${level.toUpperCase()}]`;

  try {
    if (level === "error") console.error(prefix, message, data);
    else if (level === "warn") console.warn(prefix, message, data);
    else if (level === "debug") console.debug(prefix, message, data);
    else console.log(prefix, message, data);
  } catch {
    console.log(prefix, message);
  }
}

/* =========================================================
   PUBLIC LOGGER API
   ========================================================= */

export const logger = {
  debug: (msg: string, data?: unknown) => addLog("debug", msg, data),
  info: (msg: string, data?: unknown) => addLog("info", msg, data),
  warn: (msg: string, data?: unknown) => addLog("warn", msg, data),
  error: (msg: string, data?: unknown) => addLog("error", msg, data),
};

/* =========================================================
   UTILITIES
   ========================================================= */

export function getLogs(): LogEntry[] {
  return [...logs];
}

export function clearLogs() {
  logs = [];
  notify();
}

export function onLogsChange(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* =========================================================
   OPTIONAL HELPERS (NEW)
   ========================================================= */

// filter logs by level
export function getLogsByLevel(level: LogLevel): LogEntry[] {
  return logs.filter((log) => log.level === level);
}

// get latest log
export function getLatestLog(): LogEntry | null {
  return logs.length ? logs[logs.length - 1] : null;
}

export type { LogEntry, LogLevel };